import React, { useState, useEffect } from "react";
import Notifications from "./Notifications";
import {
  subscribeToMaintenance,
  subscribeToMaintenanceRecovered,
  getMaintenanceActive,
} from "../../utils/maintenanceStatus";

const MAINTENANCE_HEADING =
  "UBT is temporarily unavailable due to planned maintenance.";
const MAINTENANCE_MESSAGE =
  "We apologize for the inconvenience. Please try again later.";

/**
 * Detects planned-maintenance API responses (HTTP 502/503) and renders an
 * inline error notification. Placed between `<Header />` and `<main>` in
 * App.tsx, alongside `OfflineBanner`.
 *
 * The Axios response interceptor in `api/axios.ts` calls
 * `reportMaintenanceError()` whenever any API call fails with a 502 (Bad
 * Gateway) or 503 (Service Unavailable) status. This component subscribes to
 * that signal via the `maintenanceStatus` pub/sub module and shows the
 * banner immediately — no polling required. `reportMaintenanceSuccess()` is
 * called on the next successful response and triggers the recovery path here.
 *
 * ## Side-effects
 * - `<main>` is hidden while maintenance is active so only the header,
 *   footer, and this banner remain visible.
 * - All subscriptions are removed on unmount.
 */
const MaintenanceBanner: React.FC = () => {
  const [isMaintenance, setIsMaintenance] = useState<boolean>(false);

  useEffect(() => {
    const mainEl = document.querySelector<HTMLElement>("main");

    const showMaintenance = (): void => {
      setIsMaintenance(true);
      if (mainEl) mainEl.style.display = "none";
    };

    const hideMaintenance = (): void => {
      setIsMaintenance(false);
      if (mainEl) mainEl.style.display = "";
    };

    const unsubscribeShow = subscribeToMaintenance(showMaintenance);
    const unsubscribeHide = subscribeToMaintenanceRecovered(hideMaintenance);

    // Initial sync: reflect whatever state Axios already knows in case a
    // 502/503 was detected before this component mounted.
    if (getMaintenanceActive()) showMaintenance();

    return (): void => {
      unsubscribeShow();
      unsubscribeHide();
      if (mainEl) mainEl.style.display = "";
    };
  }, []);

  if (!isMaintenance) return null;

  return (
    <div role="alert" className="flex! mt-20! justify-center! min-h-[40vh]! min-w-[100vh]!">
      <Notifications
        heading={MAINTENANCE_HEADING}
        messageInfo={MAINTENANCE_MESSAGE}
        variant="Error"
        widthClass="min-w-[50%]!"
      />
    </div>
  );
};

export default MaintenanceBanner;
